<template>
  <div class="entry-builder">
    <header class="journal-head">
      <span class="journal-head__title">Asiento</span>
      <span class="journal-head__meta" v-if="date">{{ formatDate(date) }}</span>
      <span class="journal-head__meta">{{ reference || 'Sin referencia' }}</span>
      <AccountingBookBadge v-if="declared === true || declared === false" :declared="declared" />
    </header>

    <!-- Carga de un renglón: el lado se sugiere según lo que falta para balancear -->
    <div class="journal-entry" v-if="showConstructor">
      <div class="journal-entry__side">
        <span class="journal-entry__label" :id="uid + '_side'">Lado</span>
        <div class="journal-entry__toggle" role="radiogroup" :aria-labelledby="uid + '_side'">
          <button type="button" role="radio" class="journal-entry__option journal-entry__option--debe"
                  :class="{ 'journal-entry__option--active': side == 'debe' }" :aria-checked="side == 'debe' ? 'true' : 'false'"
                  @click="setSide('debe')">Debe</button>
          <button type="button" role="radio" class="journal-entry__option journal-entry__option--haber"
                  :class="{ 'journal-entry__option--active': side == 'haber' }" :aria-checked="side == 'haber' ? 'true' : 'false'"
                  @click="setSide('haber')">Haber</button>
        </div>
      </div>
      <div class="journal-entry__account" ref="account" :key="'form_account_' + line.account_id + '_' + side" @keydown.capture="onAccountKeydown($event)">
        <FindObject render="search"
                    tag="Cuenta"
                    type="accountingAccountsImputation"
                    @select-object="loadAccount($event)"
                    :valueID="line.account_id"
                    :where="side == 'debe' ? conditionAccountingAccountsDebe : conditionAccountingAccountsHaber" />
      </div>
      <b-form-group label="Importe" class="journal-entry__amount">
        <b-input-group size="sm" prepend="$">
          <app-number-input ref="amount" v-model="line.amount" size="sm" @keydown.enter.prevent="addFromKeyboard()" />
        </b-input-group>
      </b-form-group>
      <b-button size="sm" variant="dark" class="journal-entry__add" @click="addLine()">
        <AppIcon name="add" class="mr-1" />Agregar al {{ side == 'debe' ? 'Debe' : 'Haber' }}
      </b-button>
    </div>

    <!-- El asiento como queda en el Libro Diario -->
    <div class="journal-table">
      <b-table-simple class="mb-0" small responsive>
        <b-thead>
          <b-tr>
            <b-th class="journal-table__code">Código</b-th>
            <b-th>Cuenta</b-th>
            <b-th class="journal-table__amount text-right">Debe</b-th>
            <b-th class="journal-table__amount text-right">Haber</b-th>
            <b-th class="journal-table__action"></b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="(item, index) in debe.detail" :key="'debe_' + index" class="journal-row journal-row--debe" :class="{ 'journal-row--zero': !toNumber(item.amount) }">
            <b-td class="journal-table__code">{{ item.account ? item.account.code : '' }}</b-td>
            <b-td>{{ item.account ? item.account.name : '' }}</b-td>
            <b-td class="text-right">
              <app-number-input v-if="editableAmounts" v-model="item.amount" size="sm" class="journal-row__input" @input="getAsiento()" />
              <span v-else class="journal-row__value">{{ money(item.amount) }}</span>
            </b-td>
            <b-td></b-td>
            <b-td class="journal-table__action">
              <b-button v-if="!item.disabled" size="sm" variant="link" class="journal-row__remove" v-b-tooltip.hover title="Quitar renglón" aria-label="Quitar renglón" @click="removeDebe(index)"><AppIcon name="close" /></b-button>
            </b-td>
          </b-tr>
          <b-tr v-for="(item, index) in haber.detail" :key="'haber_' + index" class="journal-row journal-row--haber" :class="{ 'journal-row--zero': !toNumber(item.amount) }">
            <b-td class="journal-table__code">{{ item.account ? item.account.code : '' }}</b-td>
            <b-td><span class="journal-row__credit"><span class="journal-row__a">a</span> {{ item.account ? item.account.name : '' }}</span></b-td>
            <b-td></b-td>
            <b-td class="text-right">
              <app-number-input v-if="editableAmounts" v-model="item.amount" size="sm" class="journal-row__input" @input="getAsiento()" />
              <span v-else class="journal-row__value">{{ money(item.amount) }}</span>
            </b-td>
            <b-td class="journal-table__action">
              <b-button v-if="!item.disabled" size="sm" variant="link" class="journal-row__remove" v-b-tooltip.hover title="Quitar renglón" aria-label="Quitar renglón" @click="removeHaber(index)"><AppIcon name="close" /></b-button>
            </b-td>
          </b-tr>
          <b-tr v-if="!debe.detail.length && !haber.detail.length">
            <b-td colspan="5" class="journal-table__empty">Todavía no hay renglones. Elegí el lado, la cuenta y el importe, y tocá Agregar.</b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <footer class="journal-balance" :class="'journal-balance--' + balanceState">
      <div class="journal-balance__total">
        <small>Debe</small>
        <b>{{ money(getTotalDebe) }}</b>
      </div>
      <div class="journal-balance__total">
        <small>Haber</small>
        <b>{{ money(getTotalHaber) }}</b>
      </div>
      <div class="journal-balance__state" role="status">
        <AppIcon :name="balanceState == 'ok' ? 'check' : (balanceState == 'error' ? 'info' : 'edit')" class="mr-1" />
        {{ balanceMessage }}
      </div>
    </footer>
  </div>
</template>
<script>
  import FindObject from '@/components/inc/find/findObject'
  import AccountingBookBadge from '@/components/modules/users/accounting/staff/inc/AccountingBookBadge'
  import moment from 'moment'

  export default {
    props: {
      typeDebe: {
        type: String,
        default: '',
        required: true,
      },
      typeHaber: {
        type: String,
        default: '',
        required: true,
      },
      defaultDebe: {
        type: Array,
        default: () => [],
        required: true,
      },
      defaultHaber: {
        type: Array,
        default: () => [],
        required: true,
      },
      currency: {
        type: Object,
        default: null
      },
      reference: {
        type: String,
        default: '',
        required: true,
      },
      // Solo para mostrar en el encabezado del asiento
      date: {
        type: String,
        default: '',
      },
      declared: {
        default: undefined,
      },
      amountTotal: {
        type: Number,
        default: 0,
        required: true,
      },
      validationMode: {
        type: String,
        default: 'voucher',
      },
      showConstructor: {
        type: Boolean,
        default: false,
      },
      // Lista de cuentas para completar: admite importes en cero, que no pasan al asiento.
      editableAmounts: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      FindObject,
      AccountingBookBadge,
    },
    data: () => {
      return {
        uid: 'entry_' + Math.random().toString(36).slice(2, 8),
        side: 'debe',
        line: {
          account: null,
          account_id: 0,
          amount: 0,
        },
        debe: {
          detail: [],
        },
        haber: {
          detail: [],
        },
      }
    },
    computed: {
      getCurrency() {
        return this.currency ? this.currency.name : 'ARS'
      },
      conditionAccountingAccountsDebe() {
        const conditions = [{ field: 'active', condition: true }]
        if(this.typeDebe) conditions.push({ field: 'type', condition: this.typeDebe })
        return conditions
      },
      conditionAccountingAccountsHaber() {
        const conditions = [{ field: 'active', condition: true }]
        if(this.typeHaber) conditions.push({ field: 'type', condition: this.typeHaber })
        return conditions
      },
      previewDebe() {
        return this.debe.detail.filter(element => parseFloat(element.amount))
      },
      previewHaber() {
        return this.haber.detail.filter(element => parseFloat(element.amount))
      },
      getTotalDebe() {
        return this.debe.detail.reduce((sum, element) => sum + (parseFloat(element.amount) || 0), 0)
      },
      getTotalHaber() {
        return this.haber.detail.reduce((sum, element) => sum + (parseFloat(element.amount) || 0), 0)
      },
      difference() {
        return Math.round((this.getTotalDebe - this.getTotalHaber) * 100) / 100
      },
      isEmptyManual() {
        return this.validationMode == 'manual' && !this.getTotalDebe && !this.getTotalHaber
      },
      validAsiento() {
        if(this.validationMode == 'manual') {
          return parseFloat(this.getTotalDebe.toFixed(2)) > 0 &&
                 parseFloat(this.getTotalDebe.toFixed(2)) == parseFloat(this.getTotalHaber.toFixed(2))
        }

        return parseFloat(this.getTotalDebe.toFixed(2)) == parseFloat(this.amountTotal.toFixed(2)) &&
               parseFloat(this.getTotalHaber.toFixed(2)) == parseFloat(this.amountTotal.toFixed(2))
      },
      balanceState() {
        if(this.isEmptyManual) return 'empty'
        return this.validAsiento ? 'ok' : 'error'
      },
      balanceMessage() {
        if(this.isEmptyManual) return 'Todavía no cargaste importes'
        if(this.validAsiento) return 'Asiento balanceado'
        if(this.validationMode != 'manual') return 'Debe y Haber tienen que sumar ' + this.money(this.amountTotal)
        if(this.difference > 0) return 'Falta ' + this.money(this.difference) + ' en el Haber'
        if(this.difference < 0) return 'Falta ' + this.money(-this.difference) + ' en el Debe'
        return 'Cargá importes mayores a cero'
      },
    },
    mounted() {
      this.loadAsiento()
    },
    methods: {
      formatDate(value) {
        return moment(value).format('DD/MM/YYYY')
      },
      toNumber(value) {
        return parseFloat(value) || 0
      },
      money(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: this.getCurrency }).format(value || 0)
      },
      // El buscador se vuelve a crear al elegir y su modal devuelve el foco al cerrarse: se espera a que termine.
      focusAccount() {
        setTimeout(() => {
          const input = this.$refs.account && this.$refs.account.querySelector('input')
          if(input) input.focus()
        }, 400)
      },
      // Con la cuenta elegida, Tab saltea los botones del buscador y va al importe.
      onAccountKeydown(event) {
        if(event.key != 'Tab' || event.shiftKey || !this.line.account_id || event.target.tagName != 'INPUT') return
        event.preventDefault()
        this.$refs.amount.$el.focus()
      },
      setSide(side) {
        this.side = side
      },
      loadAccount(object) {
        if(object) {
          this.line.account = object
          this.line.account_id = object.id
          this.focusAccount()
        } else {
          this.line.account = null
          this.line.account_id = 0
        }
      },
      addFromKeyboard() {
        if(this.addLine()) {
          this.focusAccount()
        }
      },
      addLine() {
        return this.side == 'debe' ? this.addDebe() : this.addHaber()
      },
      // Después de cada renglón, el lado y el importe pasan a lo que falta para balancear
      suggestNext() {
        this.line.account = null
        this.line.account_id = 0
        if(this.validationMode == 'manual' && this.difference) {
          this.side = this.difference > 0 ? 'haber' : 'debe'
          this.line.amount = Math.abs(this.difference)
        } else {
          this.line.amount = 0
        }
      },
      validateLine() {
        if(!this.line.account_id) {
          this.$awn.alert('No se cargó la Cuenta Contable')
          return false
        }
        if(!parseFloat(this.line.amount)) {
          this.$awn.alert('No se cargó el Importe para la Cuenta Contable')
          return false
        }
        return true
      },
      exceedsTotal(detail) {
        const sum = detail.reduce((total, element) => total + parseFloat(element.amount), parseFloat(this.line.amount))
        if(this.validationMode != 'manual' && sum > parseFloat(this.amountTotal.toFixed(2))) {
          this.$awn.alert('El valor ingresado no puede ser mayor al monto total de la operación')
          return true
        }
        return false
      },
      addDebe() {
        if(!this.validateLine() || this.exceedsTotal(this.debe.detail)) return false
        this.debe.detail.push({ account: this.line.account, account_id: this.line.account_id, amount: this.line.amount })
        this.suggestNext()
        this.getAsiento()
        return true
      },
      removeDebe(index) {
        this.debe.detail.splice(index, 1)
        this.getAsiento()
      },
      addHaber() {
        if(!this.validateLine() || this.exceedsTotal(this.haber.detail)) return false
        this.haber.detail.push({ account: this.line.account, account_id: this.line.account_id, amount: this.line.amount })
        this.suggestNext()
        this.getAsiento()
        return true
      },
      removeHaber(index) {
        this.haber.detail.splice(index, 1)
        this.getAsiento()
      },
      // Agrupa las cuentas repetidas de lo que llega precargado
      mergeDefaults(source, target) {
        (source || []).forEach(element => {
          if(!parseFloat(element.amount) && !this.editableAmounts) return

          const existing = target.find(row => element.account.id == row.account_id && element.disabled == row.disabled)
          if(existing) {
            existing.amount = parseFloat(existing.amount) + parseFloat(element.amount)
          } else {
            target.push({
              account: element.account,
              account_id: element.account.id,
              amount: parseFloat(element.amount),
              disabled: element.disabled,
            })
          }
        })
      },
      loadAsiento() {
        this.mergeDefaults(this.defaultDebe, this.debe.detail)
        this.mergeDefaults(this.defaultHaber, this.haber.detail)
        if(this.validationMode == 'manual' && this.difference > 0) {
          this.side = 'haber'
        }
        this.getAsiento()
      },
      // Todos los renglones, también los que están en cero: para guardar un asiento modelo
      currentLines() {
        const rows = []
        this.debe.detail.forEach(element => rows.push({ accounting_accounts_id: element.account_id, type: 'Debe', amount: parseFloat(element.amount) || 0 }))
        this.haber.detail.forEach(element => rows.push({ accounting_accounts_id: element.account_id, type: 'Haber', amount: parseFloat(element.amount) || 0 }))
        return rows
      },
      getAsiento() {
        if(!this.validAsiento) {
          this.$emit('getAsiento', null)
          return
        }

        const asiento = []
        this.previewDebe.forEach(element => {
          asiento.push({ account: element.account, account_id: element.account_id, amount: element.amount, type: 'debe' })
        })
        this.previewHaber.forEach(element => {
          asiento.push({ account: element.account, account_id: element.account_id, amount: element.amount, type: 'haber' })
        })
        this.$emit('getAsiento', asiento)
      },
    },
  }
</script>
<style scoped>
  .entry-builder {
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface);
  }
  .journal-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    padding: 8px 12px;
    background: var(--app-color-surface-subtle);
    border-bottom: 1px solid var(--app-color-border);
    border-radius: 8px 8px 0 0;
  }
  .journal-head__title {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .journal-head__meta {
    font-size: 13px;
    color: var(--app-color-text-secondary);
  }
  .journal-head__meta::before {
    content: '·';
    margin-right: 10px;
  }
  .journal-entry {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 8px 12px;
    padding: 10px 12px 0;
    border-bottom: 1px solid var(--app-color-border);
  }
  .journal-entry__side {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
  }
  .journal-entry__label {
    font-size: 14px;
    margin-bottom: 0.5rem;
  }
  .journal-entry__toggle {
    display: inline-flex;
    border: 1px solid var(--app-color-border-strong);
    border-radius: 6px;
    overflow: hidden;
  }
  .journal-entry__option {
    padding: 4px 12px;
    font-size: 13px;
    font-weight: 600;
    color: var(--app-color-text-secondary);
    background: var(--app-color-surface);
    border: 0;
    cursor: pointer;
  }
  .journal-entry__option + .journal-entry__option {
    border-left: 1px solid var(--app-color-border);
  }
  .journal-entry__option:focus-visible {
    outline: 2px solid var(--app-color-primary);
    outline-offset: -2px;
  }
  .journal-entry__option--active.journal-entry__option--debe {
    color: var(--app-color-white);
    background: var(--app-color-info);
  }
  .journal-entry__option--active.journal-entry__option--haber {
    color: var(--app-color-white);
    background: var(--app-color-success);
  }
  .journal-entry__account {
    flex: 1 1 260px;
    min-width: 0;
  }
  .journal-entry__amount {
    flex: 0 0 160px;
  }
  .journal-entry__add {
    flex: 0 0 auto;
    margin-bottom: 1rem;
  }
  .journal-table {
    max-height: 360px;
    overflow-y: auto;
  }
  .journal-table th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--app-color-text-secondary);
    background: var(--app-color-surface);
    border-top: 0;
    border-bottom: 2px solid var(--app-color-border-strong);
  }
  .journal-table td {
    vertical-align: middle;
    font-size: 14px;
  }
  .journal-table__code {
    width: 110px;
    padding-left: 14px !important;
    color: var(--app-color-text-muted);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .journal-table__amount {
    width: 18%;
  }
  .journal-table__action {
    width: 36px;
    text-align: right;
  }
  .journal-table__empty {
    padding: 16px 12px !important;
    color: var(--app-color-text-muted);
    text-align: center;
    font-size: 13px !important;
  }
  .journal-row--debe td:first-child {
    box-shadow: inset 4px 0 0 var(--app-color-info);
  }
  .journal-row--haber td:first-child {
    box-shadow: inset 4px 0 0 var(--app-color-success);
  }
  .journal-row--zero td {
    color: var(--app-color-text-muted);
  }
  .journal-row--zero td:first-child {
    box-shadow: none;
  }
  .journal-row__credit {
    display: inline-block;
    padding-left: 28px;
  }
  .journal-row__a {
    font-style: italic;
    color: var(--app-color-text-muted);
  }
  .journal-row__value {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .journal-row__input {
    width: 140px;
    margin-left: auto;
    text-align: right;
    font-weight: 600;
  }
  .journal-row__remove {
    padding: 2px 4px;
    color: var(--app-color-danger);
  }
  .journal-balance {
    position: sticky;
    bottom: 0;
    z-index: 2;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 24px;
    padding: 10px 12px;
    border-top: 2px solid var(--app-color-border-strong);
    border-radius: 0 0 8px 8px;
    background: var(--app-color-surface-subtle);
  }
  .journal-balance__total small {
    display: block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--app-color-text-secondary);
  }
  .journal-balance__total b {
    font-size: 18px;
    font-variant-numeric: tabular-nums;
  }
  .journal-balance__state {
    margin-left: auto;
    padding: 4px 12px;
    font-size: 14px;
    font-weight: 700;
    border-radius: 999px;
  }
  .journal-balance--ok .journal-balance__state {
    color: var(--app-color-success);
    background: var(--app-color-success-soft);
  }
  .journal-balance--error .journal-balance__state {
    color: var(--app-color-danger);
    background: var(--app-color-danger-soft);
  }
  .journal-balance--empty .journal-balance__state {
    color: var(--app-color-info);
    background: var(--app-color-info-soft);
  }
  @media (max-width: 575.98px) {
    .journal-entry__account {
      flex-basis: 100%;
    }
    .journal-entry__amount {
      flex: 1 1 auto;
    }
    .journal-balance__state {
      margin-left: 0;
    }
  }
</style>
